import type { FabricEditor } from 'modules/fabric-editor';
import { rebaseAssetUrls } from 'modules/manager/domain/template-file';
import { selectActiveTemplate } from 'modules/manager/store';
import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'store/hooks';

export interface UseRestoreTemplateResult {
  /**
   * Объекты текущего шаблона легли в канву (`canvas:loaded` восстановления прошёл).
   * До этого канва пустая — редактор держит скелетон. Сбрасывается при смене
   * `template.id`: копия, открытая из редактора, тоже показывается со скелетоном.
   */
  isRestored: boolean;
}

export function useRestoreTemplate(editor: FabricEditor | null): UseRestoreTemplateResult {
  const template = useAppSelector(selectActiveTemplate);
  const restoredFor = useRef<number | null>(null);
  const [restoredId, setRestoredId] = useState<number | null>(null);

  useEffect(() => {
    if (!editor || !template) return;
    if (restoredFor.current === template.id) return;
    restoredFor.current = template.id;

    const templateId = template.id;
    // Логотип сериализуется абсолютным URL стенда, где шаблон сохранили (в т.ч. localhost
    // против тестового бэкенда), — без переноса на текущий origin вместо него пустая плашка.
    const objects = rebaseAssetUrls(template.objects ?? [], window.location.origin);
    const payload = { version: template.version, objects };
    editor.canvas.loadFromJSON(payload, () => {
      editor.canvas.renderAll();
      editor.emit('canvas:loaded', { source: 'restore' });
      editor.history?.clear();
      editor.dirty?.markClean();
      setRestoredId(templateId);
    });
  }, [editor, template]);

  return { isRestored: template != null && restoredId === template.id };
}
